import React, { createContext, Dispatch, SetStateAction, useCallback, useContext, useMemo, useState } from 'react';
import { Error } from '@cognitiv/cicada-ui';
import { UnionOfSingleKeyObjects } from 'ducks/types/type-helpers';
import { useValidate } from 'hooks/useValidation';
import { default_job, JobFormState, run_validation } from 'modals/job-definition/constants';
import { handleTimeoutConversion } from 'utils/helpers';

interface JobDefinitionContextType {
  form: JobFormState;
  errors: Error[];
  validate: () => boolean;
  setForm: Dispatch<SetStateAction<JobFormState>>;
  onChange: (item: Partial<JobFormState>) => void;
  updateJobDefinitionFormDefault: (payload: UnionOfSingleKeyObjects<JobFormState>) => void;
  onTimeoutSecondsChange: (input: { value: string }) => void;
  onPreventPreemptionSelect: (item: { name: string; value: boolean; uuid: string }) => void;
  onCPUArchitectureSelect: (item: JobFormState) => void;
  onLocationGroupSelect: (item: JobFormState) => void;
  onResultMethodSelect: (item: JobFormState) => void;
}

interface JobDefinitionProviderProps {
  children: React.ReactNode;
}

export const JobDefinitionContext = createContext<JobDefinitionContextType>({} as JobDefinitionContextType);

export function useJobDefinitionContext() {
  return useContext(JobDefinitionContext);
}

export const JobDefinitionContextProvider = ({ children }: JobDefinitionProviderProps) => {
  const [form, setForm] = useState<JobFormState>({ ...default_job });
  const { errors, validate, resetFieldError } = useValidate(form, run_validation);

  const onChange = useCallback(
    (item: Partial<JobFormState>) => {
      resetFieldError(item);
      setForm((form) => ({ ...form, ...item }));
    },
    [resetFieldError],
  );

  const updateJobDefinitionFormDefault = useCallback((payload: UnionOfSingleKeyObjects<JobFormState>) => {
    setForm((form) => ({ ...form, ...payload }));
  }, []);

  const onTimeoutSecondsChange = useCallback(
    (input: { value: string }) => {
      onChange({
        timeout_seconds: handleTimeoutConversion(input.value),
        timeout_seconds_masked: input.value,
      });
    },
    [onChange],
  );

  const onPreventPreemptionSelect = useCallback(
    (item: { name: string; value: boolean; uuid: string }) => {
      onChange({
        prevent_preemption_name: item.name,
        prevent_preemption: item.value,
      });
    },
    [onChange],
  );

  const onCPUArchitectureSelect = useCallback(
    (item: JobFormState) => {
      onChange({
        cpu_architecture_name: item.cpu_architecture_name,
        cpu_architecture_id: item.cpu_architecture_id,
      });
    },
    [onChange],
  );

  const onLocationGroupSelect = useCallback(
    (item: JobFormState) => {
      onChange({
        location_group_name: item.location_group_name,
        location_group_id: item.location_group_id,
      });
    },
    [onChange],
  );

  const onResultMethodSelect = useCallback(
    (item: JobFormState) => {
      onChange({
        stdout_success_text: '',
        result_method_name: item.result_method_name,
        result_method_id: item.result_method_id,
      });
    },
    [onChange],
  );

  const value = useMemo(
    () => ({
      form,
      errors,
      validate,
      setForm,
      onChange,
      updateJobDefinitionFormDefault,
      onTimeoutSecondsChange,
      onPreventPreemptionSelect,
      onCPUArchitectureSelect,
      onLocationGroupSelect,
      onResultMethodSelect,
    }),
    [
      form,
      errors,
      validate,
      onChange,
      updateJobDefinitionFormDefault,
      onTimeoutSecondsChange,
      onPreventPreemptionSelect,
      onCPUArchitectureSelect,
      onLocationGroupSelect,
      onResultMethodSelect,
    ],
  );

  return <JobDefinitionContext.Provider value={value}>{children}</JobDefinitionContext.Provider>;
};
